import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { Toaster, toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { FormState } from './types';
import { initialFormState } from './data/mockData';
import { validateForm } from './utils/readiness';
import { useTheme } from './utils/useTheme';
const DRAFT_KEY = 'disclosureRequestDraft';
import { Header } from './components/Header';
import { NavRail } from './components/NavRail';
import { ReadinessChecklist } from './components/ReadinessChecklist';
import { SectionCard } from './components/SectionCard';
import { ContactsSection } from './components/sections/ContactsSection';
import { PropertyTransaction } from './components/sections/PropertyTransaction';
import { LoanProduct } from './components/sections/LoanProduct';
import { DisclosureDetails } from './components/sections/DisclosureDetails';
import { Notes } from './components/sections/Notes';
import { ReviewSubmit } from './components/sections/ReviewSubmit';
import { NotificationSummary } from './components/sections/NotificationSummary';
export function App() {
  const [state, setState] = useState<FormState>(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return {
        ...initialFormState,
        ...JSON.parse(saved)
      };
    } catch (e) {
      /* ignore unavailable storage */
    }
    return initialFormState;
  });
  const [activeSection, setActiveSection] = useState<string>('contacts');
  const [showChecklist, setShowChecklist] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const {
    theme,
    toggleTheme
  } = useTheme();
  const validation = useMemo(() => validateForm(state), [state]);
  const outstandingCount = validation.checklist.filter(i => i.status !== 'Complete').length;
  const handleChange = (field: keyof FormState, value: any) => {
    setState(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleSaveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(state));
      setLastSavedAt(new Date());
      toast.success('Draft saved', {
        description: 'Your progress is saved. You can return to it later.'
      });
    } catch (e) {
      toast.error('Unable to save draft', {
        description: 'Local storage is not available in this environment.'
      });
    }
  };
  const handleSubmit = () => {
    if (validation.canSubmit) {
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* ignore */
      }
      setLastSavedAt(null);
      toast.success('Disclosure Request Submitted Successfully!', {
        description: 'The team has been notified.'
      });
    }
  };
  // Let the user know a saved draft was restored on load.
  useEffect(() => {
    try {
      if (localStorage.getItem(DRAFT_KEY)) {
        toast.info('Draft restored', {
          description: 'We loaded your previously saved progress.'
        });
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Measure the sticky header so the nav rail can sit just below it.
  useLayoutEffect(() => {
    const measure = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  // Intersection Observer to update active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    const sections = ['contacts', 'property', 'product', 'disclosure', 'notes', 'review'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const navSteps = [{
    id: 'contacts',
    label: 'Required Contacts',
    status: validation.sections.contacts
  }, {
    id: 'property',
    label: 'Property & Transaction',
    status: validation.sections.property
  }, {
    id: 'product',
    label: 'Loan Product',
    status: validation.sections.product
  }, {
    id: 'disclosure',
    label: 'Disclosure Details',
    status: validation.sections.disclosure
  }, {
    id: 'notes',
    label: 'Notes',
    status: validation.sections.notes
  }, {
    id: 'review',
    label: 'Review & Submit',
    status: validation.canSubmit ? 'Complete' : 'Blocked'
  }];
  const failingHardStops = validation.hardStops.filter(h => h.status !== 'Complete');
  return <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans transition-colors" data-id="element-0">
      <Toaster position="top-center" richColors theme={theme} data-id="element-1" />

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 pb-4 md:pb-6 lg:pb-8" style={{
      ['--header-h' as any]: `${headerHeight + 16}px`
    }} data-id="element-2">
        <div ref={headerRef} className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900 pt-4 md:pt-6 lg:pt-8 transition-colors" data-id="element-3">
          <Header state={state} canSubmit={validation.canSubmit} onSubmit={handleSubmit} onSaveDraft={handleSaveDraft} lastSavedAt={lastSavedAt} outstandingCount={outstandingCount} onOpenChecklist={() => setShowChecklist(true)} theme={theme} onToggleTheme={toggleTheme} data-id="element-4" />
        </div>

        <div className="flex flex-col lg:flex-row gap-8 relative items-stretch" data-id="element-5">
          {/* Left Nav Rail */}
          <NavRail steps={navSteps as any} activeSection={activeSection} onStepClick={scrollToSection} topOffset={headerHeight + 16} data-id="element-6" />

          {/* Main Workspace */}
          <main className="flex-1 min-w-0 pb-32" data-id="element-7">
            {/* Automatic system hard stops */}
            {failingHardStops.length > 0 && <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-5" data-id="element-8">
                <div className="flex items-start space-x-3" data-id="element-9">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" data-id="element-10" />
                  <div data-id="element-11">
                    <h2 className="text-sm font-semibold text-red-800 dark:text-red-300" data-id="element-12">
                      Submission blocked by automatic system checks
                    </h2>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-400/80" data-id="element-13">
                      The system detected the following blocking issues. These
                      must be resolved upstream before this request can be
                      submitted.
                    </p>
                    <ul className="mt-3 space-y-1.5" data-id="element-14">
                      {failingHardStops.map(h => <li key={h.id} className="flex items-start text-sm text-red-700 dark:text-red-400/90" data-id="element-15">
                          <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500 dark:bg-red-400" data-id="element-16" />
                          <span data-id="element-17">
                            <span className="font-medium" data-id="element-18">{h.label}:</span>{' '}
                            {h.explanation}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>}

            <SectionCard id="contacts" title="Required Contacts" status={validation.sections.contacts} data-id="element-19">
              <ContactsSection state={state} onChange={handleChange} data-id="element-20" />
            </SectionCard>

            <SectionCard id="property" title="Property & Transaction" status={validation.sections.property} data-id="element-21">
              <PropertyTransaction state={state} onChange={handleChange} data-id="element-22" />
            </SectionCard>

            <SectionCard id="product" title="Loan Product" status={validation.sections.product} data-id="element-23">
              <LoanProduct state={state} onChange={handleChange} data-id="element-24" />
            </SectionCard>

            <SectionCard id="disclosure" title="Disclosure Details" status={validation.sections.disclosure} data-id="element-25">
              <DisclosureDetails state={state} onChange={handleChange} data-id="element-26" />
            </SectionCard>

            <SectionCard id="notes" title="Notes" status={validation.sections.notes} data-id="element-27">
              <Notes state={state} onChange={handleChange} data-id="element-28" />
            </SectionCard>

            <SectionCard id="review" title="Review & Submit" status={validation.canSubmit ? 'Complete' : 'Blocked'} data-id="element-29">
              <div className="space-y-8" data-id="element-30">
                <ReviewSubmit state={state} sectionStatuses={validation.sections} onEditSection={scrollToSection} data-id="element-31" />
                <NotificationSummary state={state} data-id="element-32" />
              </div>
            </SectionCard>
          </main>
        </div>
      </div>

      {/* Readiness Checklist drawer */}
      <ReadinessChecklist items={validation.checklist} open={showChecklist} onClose={() => setShowChecklist(false)} onFixNow={scrollToSection} data-id="element-33" />
    </div>;
}